// src/pages/MechanicReport.jsx
import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Select,
  Button,
  Space,
  Typography,
  Spin,
} from "antd";
import axios from "axios";
import Swal from "sweetalert2";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { BASE_URL } from "../../App";

const { Option } = Select;
const { Title } = Typography;

// Local file uploaded (used as logo/header image)
const LOGO_URL = "/mnt/data/45bbc8fe-0661-4fd4-b13d-5b34604dadd2.png";

const swalOptions = (opts = {}) =>
  Swal.fire({
    showClass: { popup: "animate__animated animate__fadeInDown" },
    hideClass: { popup: "animate__animated animate__fadeOutUp" },
    ...opts,
  });

export default function MechanicReport() {
  const [data, setData] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [dealerList, setDealerList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [dealersLoading, setDealersLoading] = useState(false);

  const usertype = localStorage.getItem("usertype") || ""; // "1","2","3"
  const login_id = localStorage.getItem("LOGIN_ID") || "";

  const [filters, setFilters] = useState({
    branch: "",
    dealer: "",
  });

  const [errors, setErrors] = useState({
    branch: false,
    dealer: false,
  });

  // ---------- Fetch branches ----------
  const fetchBranches = async () => {
    setBranchesLoading(true);
    try {
      const res = await axios.post(BASE_URL + "/field_branches", {
        usertype,
        login_id,
      });

      if (res.data && res.data.status === false && res.data.fields) {
        swalOptions({
          icon: "warning",
          title: "Missing Fields",
          html: `<b>${res.data.fields.join(", ")}</b> is required`,
        });
        setBranchList([]);
        return;
      }

      setBranchList(res.data || []);
    } catch (err) {
      swalOptions({
        icon: "error",
        title: "Error",
        text: "Failed to load branches",
      });
      setBranchList([]);
    } finally {
      setBranchesLoading(false);
    }
  };

  // ---------- Fetch dealers ----------
  const fetchDealers = async (branchValue) => {
    if (!branchValue) {
      setDealerList([]);
      return;
    }
    setDealersLoading(true);
    try {
      const res = await axios.post(BASE_URL + "/field_dealers", {
        branch: branchValue,
        usertype,
        login_id,
      });

      if (res.data && res.data.status === false && res.data.fields) {
        swalOptions({
          icon: "warning",
          title: "Missing Fields",
          html: `<b>${res.data.fields.join(", ")}</b> is required`,
        });
        setDealerList([]);
        return;
      }

      setDealerList(res.data || []);
    } catch (err) {
      swalOptions({
        icon: "error",
        title: "Error",
        text: "Failed to load dealers",
      });
      setDealerList([]);
    } finally {
      setDealersLoading(false);
    }
  };

  // ---------- Fetch report ----------
  const fetchData = async () => {
    // validation: for usertype 2 or 3 require branch & dealer selection (unless you want ALL)
    setErrors({ branch: false, dealer: false });

    if ((usertype === "2" || usertype === "3") && !filters.branch) {
      setErrors((s) => ({ ...s, branch: true }));
      swalOptions({
        icon: "warning",
        title: "Validation",
        text: "Please select Branch",
      });
      return;
    }
    if ((usertype === "2" || usertype === "3") && !filters.dealer) {
      setErrors((s) => ({ ...s, dealer: true }));
      swalOptions({
        icon: "warning",
        title: "Validation",
        text: "Please select Dealer",
      });
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(BASE_URL + "/field_partner_report", {
        branch: filters.branch,
        dealer: filters.dealer,
        usertype,
        login_id,
      });

      if (res.data && res.data.status === false && res.data.fields) {
        swalOptions({
          icon: "warning",
          title: "Missing Fields",
          html: `<b>${res.data.fields.join(", ")}</b> is required`,
        });
        setData([]);
        return;
      }

      if (!Array.isArray(res.data) || res.data.length === 0) {
        swalOptions({
          icon: "info",
          title: "No Data",
          text: "No records found for selected criteria",
        });
        setData([]);
        return;
      }

      setData(res.data);
      // swalOptions({
      //   icon: "success",
      //   title: "Report Loaded",
      //   showConfirmButton: false,
      //   timer: 900,
      // });
    } catch (err) {
      swalOptions({
        icon: "error",
        title: "Error",
        text: "Something went wrong while loading the report!",
      });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  // ---------- Download Excel ----------
  const downloadExcel = () => {
    if (!data || data.length === 0) {
      swalOptions({
        icon: "info",
        title: "No Data",
        text: "No records to export!",
      });
      return;
    }

    // Normalize columns (optional rename)
    const exportData = data.map((row) => ({
      Branch: row.Branch,
      ASC_Code: row.ASC_Code,
      ASC_Name: row.ASC_Name,
      Field_Partner_Name: row.Field_Partner_Name,
      Mobile_Number: row.Mobile_Number,
      Date_Of_Birth: row.Date_Of_Birth,
      Address: row.Address,
      Type_of_Role: row.Type_of_Role,
      Status: row.Status,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Mechanic Report");
    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(blob, `mechanic_report_${new Date().toISOString().slice(0, 19)}.xlsx`);
  };

  // ---------- Effects ----------
  useEffect(() => {
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // whenever branch changes, load dealers (including ALL behavior)
    if (filters.branch) {
      fetchDealers(filters.branch);
    } else {
      setDealerList([]);
    }
    // reset selected dealer when branch changes
    setFilters((f) => ({ ...f, dealer: "" }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.branch]);

  // ---------- Columns ----------
  const columns = [
    { title: "Branch", dataIndex: "Branch", key: "Branch" },
    { title: "ASC Code", dataIndex: "ASC_Code", key: "ASC_Code" },
    { title: "ASC Name", dataIndex: "ASC_Name", key: "ASC_Name" },
    {
      title: "Field Partner",
      dataIndex: "Field_Partner_Name",
      key: "Field_Partner_Name",
    },
    { title: "Mobile", dataIndex: "Mobile_Number", key: "Mobile_Number" },
    { title: "DOB", dataIndex: "Date_Of_Birth", key: "Date_Of_Birth" },
    { title: "Address", dataIndex: "Address", key: "Address" },
    { title: "Type of Role", dataIndex: "Type_of_Role", key: "Type_of_Role" },
    { title: "Status", dataIndex: "Status", key: "Status" },
  ];

  // ---------- UI ----------
  return (
    <div style={{ padding: 20 }}>
      <Card
        style={{ borderRadius: 12, boxShadow: "0 8px 30px rgba(0,0,0,0.08)" }}
        bodyStyle={{ padding: 16 }}
      >
        <Row align="middle" justify="space-between">
          <Col>
            <Space align="center">
              {/* <img
                src={LOGO_URL}
                alt="logo"
                style={{ height: 48, width: 48, borderRadius: 8 }}
              />
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  Mechanic Dealer Report
                </Title>
                <div style={{ color: "#666", fontSize: 12 }}>
                  Filter & export mechanics / field partners
                </div>
              </div> */}
            </Space>
          </Col>

          <Col>
            <Space>
              <Button
                onClick={downloadExcel}
                type="primary"
                style={{ background: "#16a34a", borderColor: "#16a34a" }}
                disabled={!data || data.length === 0}
              >
                Download Excel
              </Button>
              <Button onClick={() => { setData([]); setFilters({ branch: "", dealer: "" }); }}>
                Reset
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card
        style={{ marginTop: 16, borderRadius: 12 }}
        bodyStyle={{ padding: 16 }}
        title="Filters"
      >
        <Row gutter={[16, 12]}>
          <Col xs={24} sm={12} md={8}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
              Branch
            </label>
            <Select
              showSearch
              placeholder="Select Branch"
              value={filters.branch || undefined}
              onChange={(val) => setFilters((s) => ({ ...s, branch: val }))}
              loading={branchesLoading}
              style={{ width: "100%" }}
              optionFilterProp="children"
              notFoundContent={branchesLoading ? <Spin /> : "No branches"}
              dropdownMatchSelectWidth={false}
              className={errors.branch ? "ant-select-border-error" : ""}
            >
              <Option value="">Select Branch</Option>
              {(usertype === "1" || usertype === "2") && <Option value="ALL">ALL</Option>}
              {branchList.map((b) => (
                <Option key={b.code} value={b.code}>
                  {b.code} - {b.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
              Dealer
            </label>
            <Select
              showSearch
              placeholder="Select Dealer"
              value={filters.dealer || undefined}
              onChange={(val) => setFilters((s) => ({ ...s, dealer: val }))}
              loading={dealersLoading}
              disabled={!filters.branch}
              style={{ width: "100%" }}
              optionFilterProp="children"
              notFoundContent={dealersLoading ? <Spin /> : "No dealers"}
              dropdownMatchSelectWidth={false}
              className={errors.dealer ? "ant-select-border-error" : ""}
            >
              <Option value="">Select Dealer</Option>
              {(usertype === "1" || usertype === "2") && filters.branch && (
                <Option value="ALL">ALL</Option>
              )}
              {dealerList.map((d) => (
                <Option key={d.dealer_code} value={d.dealer_code}>
                  {d.dealer_code} - {d.dealer_name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col xs={24} sm={24} md={8} style={{ display: "flex", alignItems: "flex-end" }}>
            <Space>
              <Button
                type="primary"
                onClick={fetchData}
                loading={loading}
                disabled={
                  (usertype === "2" || usertype === "3")
                    ? (!filters.branch || !filters.dealer)
                    : false
                }
              >
                Load Report
              </Button>

              <Button onClick={() => { setData([]); setFilters({ branch: "", dealer: "" }); }}>
                Clear
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card style={{ marginTop: 16, borderRadius: 12 }}>
        <Table
          columns={columns}
          dataSource={data}
          loading={loading}
          rowKey={(r, idx) => `${r.ASC_Code || idx}-${idx}`}
          pagination={{ pageSize: 15 }}
          scroll={{ x: 900 }}
        />
      </Card>
    </div>
  );
}
